import { localDate } from '@/platform/lib/utils';
import {
    Badge,
    Button,
    Input,
    Message,
    Select,
    Table,
    TableBody,
    TableCell,
    TableRow,
    Textarea,
} from '@/platform/ui';
import {
    Disclosure,
    DisclosureButton,
    DisclosurePanel,
} from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { useForm, usePage } from '@inertiajs/react';
import SidePanel, { closePanel } from './SidePanel';

// Domain\EmployeeCommunications\Enums\PhoneCallState
const VOICEMAIL = 2;
const NO_RESPONSE = 3;
const INVALID_NUMBER = 6;
const LANGUAGE = 7;
const WONT_COOPERATE = 8;
const NO_ACTION = 9;
const COMPLETED_BY_OPS = 12;
const QUESTIONNAIRE_WRONGLY_ANSWERED = 13;
const REMINDER_SENT = 14;
const COMPLETED_BY_OPS_NB = 15;

const CONTACT_FIELDS = [
    { key: 'email', label: 'E-mailadres', type: 'email' },
    { key: 'phone_number', label: 'Telefoonnummer vast', type: 'tel' },
    { key: 'phone_number_work', label: 'Telefoonnummer werk', type: 'tel' },
    { key: 'mobile_number', label: 'Telefoonnummer mobiel', type: 'tel' },
    {
        key: 'mobile_number_work',
        label: 'Telefoonnummer mobiel werk',
        type: 'tel',
    },
];

const buildOutcomes = phoneCall => {
    const subject = phoneCall.is_authorisation ? 'machtiging' : 'vragenlijst';

    return [
        {
            group: 'Niet bereikt',
            items: [
                {
                    state: VOICEMAIL,
                    label: 'Voicemail',
                    effect: 'Na drie keer voicemail wordt de werknemer geëscaleerd.',
                },
                {
                    state: NO_RESPONSE,
                    label: 'Geen gehoor',
                    effect: 'Morgen wordt automatisch opnieuw gebeld.',
                },
                {
                    state: INVALID_NUMBER,
                    label: 'Telefoonnummer onjuist',
                    effect: 'De werknemer wordt geëscaleerd en alle geplande belletjes vervallen.',
                    danger: true,
                },
                {
                    state: LANGUAGE,
                    label: 'Taalbarrière',
                    effect: 'De werknemer wordt geëscaleerd.',
                    danger: true,
                },
            ],
        },
        {
            group: 'Gesproken',
            items: [
                {
                    state: COMPLETED_BY_OPS,
                    label: phoneCall.is_authorisation
                        ? 'Machtiging ondertekenen namens werknemer'
                        : 'Vragenlijst invullen namens werknemer',
                    effect: `Na opslaan opent de ${subject} in een nieuw tabblad.`,
                    submitLabel: `Opslaan en ${subject} openen`,
                    opensActionLink: true,
                },
                !phoneCall.is_authorisation && {
                    state: COMPLETED_BY_OPS_NB,
                    label: 'Werknemer heeft geen voordelen',
                    effect: 'Alle lopende communicatie voor deze werknemer stopt.',
                },
                {
                    state: REMINDER_SENT,
                    label: `Reminder ${subject} versturen`,
                    effect: 'De werknemer ontvangt direct een reminder per e-mail.',
                },
                phoneCall.can_report_wrong_answers && {
                    state: QUESTIONNAIRE_WRONGLY_ANSWERED,
                    label: 'Vragenlijst verkeerd beantwoord',
                    effect: 'Alle lopende communicatie voor deze werknemer stopt.',
                    danger: true,
                },
                {
                    state: WONT_COOPERATE,
                    label: 'Werknemer wil niet meewerken',
                    effect: 'Alle lopende communicatie stopt en de werknemer wordt geëscaleerd.',
                    danger: true,
                },
                {
                    state: NO_ACTION,
                    label: 'Gesproken, geen actie nodig',
                    effect: 'De planning blijft ongewijzigd.',
                },
            ].filter(Boolean),
        },
    ];
};

const Collapsible = ({ title, defaultOpen = false, children }) => (
    <Disclosure
        as='div'
        defaultOpen={defaultOpen}
        className='rounded-lg border border-gray-200'
    >
        <DisclosureButton className='group focus-visible:outline-primary-600 flex w-full items-center justify-between gap-3 px-4 py-3 text-left text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 focus-visible:outline-2 focus-visible:outline-offset-2'>
            {title}
            <ChevronDownIcon
                aria-hidden='true'
                className='size-5 shrink-0 text-gray-400 transition-transform group-data-open:rotate-180'
            />
        </DisclosureButton>
        <DisclosurePanel className='flex flex-col gap-4 border-t border-gray-200 p-4'>
            {children}
        </DisclosurePanel>
    </Disclosure>
);

const DetailRow = ({ label, children }) => (
    <TableRow>
        <TableCell className='font-bold'>{label}</TableCell>
        <TableCell textAlign='right'>{children}</TableCell>
    </TableRow>
);

const PhoneCallPanel = () => {
    const {
        auth,
        employee,
        base_employee: baseEmployee,
        communication,
        phone_call: phoneCall,
    } = usePage().props;

    const calls = communication.phone_communications ?? [];
    const canEditContact = auth?.permissions?.includes('update employees');

    const contact = useForm(
        Object.fromEntries(
            CONTACT_FIELDS.map(({ key }) => [key, communication[key] || ''])
        )
    );

    const outcomes = buildOutcomes(phoneCall);
    const outcome = useForm({ state: '', note: '' });
    const selected = outcomes
        .flatMap(({ items }) => items)
        .find(item => item.state === Number(outcome.data.state));

    const baseUrl = `/customers/${employee.customer_id}/employees/${employee.id}/communications`;
    const close = () => closePanel(baseUrl);

    const saveContact = event => {
        event.preventDefault();

        contact.patch(`${baseUrl}/${communication.id}`, {
            preserveScroll: true,
        });
    };

    const saveOutcome = event => {
        event.preventDefault();

        outcome.post(`${baseUrl}/${communication.id}/phone`, {
            preserveScroll: true,
            onSuccess: () => {
                outcome.reset();

                if (selected?.opensActionLink && phoneCall.action_link) {
                    window.open(
                        phoneCall.action_link,
                        '_blank',
                        'noopener,noreferrer'
                    );
                }
            },
        });
    };

    return (
        <SidePanel
            title={
                <>
                    {employee.name}
                    <Badge color={phoneCall.dgr_positive ? 'green' : 'red'}>
                        DGR
                    </Badge>
                </>
            }
            subtitle={[baseEmployee.customer?.name, baseEmployee.entity?.name]
                .filter(Boolean)
                .join(' · ')}
            onClose={close}
        >
            {phoneCall.private_numbers_forbidden && (
                <Message variant='warning'>
                    Gebruik van privénummers is verboden in de
                    entiteitsinstellingen.
                </Message>
            )}

            <Collapsible title='Werknemer' defaultOpen>
                <form onSubmit={saveContact}>
                    <Table striped>
                        <TableBody>
                            <DetailRow label='Type'>
                                {communication.type_label}
                            </DetailRow>
                            <DetailRow label='Categorie'>
                                {communication.sub_type_label}
                            </DetailRow>
                            <DetailRow label='BSN'>{employee.bsn}</DetailRow>
                            <DetailRow label='Geboortedatum'>
                                {localDate(employee.birth_date)}
                            </DetailRow>
                            <DetailRow label='Datum in dienst'>
                                {localDate(phoneCall.employment_start)}
                            </DetailRow>
                            <DetailRow label='Datum uit dienst'>
                                {localDate(phoneCall.employment_end)}
                            </DetailRow>
                            {CONTACT_FIELDS.map(({ key, label, type }) => (
                                <DetailRow key={key} label={label}>
                                    <Input
                                        type={type}
                                        value={contact.data[key]}
                                        readOnly={!canEditContact}
                                        onChange={event =>
                                            contact.setData(
                                                key,
                                                event.target.value
                                            )
                                        }
                                        error={contact.errors[key]}
                                    />
                                </DetailRow>
                            ))}
                        </TableBody>
                    </Table>
                    {canEditContact && (
                        <div className='mt-4 flex justify-end'>
                            <Button
                                type='submit'
                                variant='primary'
                                loading={contact.processing}
                                disabled={!contact.isDirty}
                            >
                                Opslaan
                            </Button>
                        </div>
                    )}
                </form>
            </Collapsible>

            <Collapsible title='Belresultaat vastleggen' defaultOpen>
                <form onSubmit={saveOutcome} className='flex flex-col gap-4'>
                    <Select
                        label='Uitkomst'
                        placeholder='Kies een uitkomst'
                        value={outcome.data.state}
                        onChange={event =>
                            outcome.setData('state', event.target.value)
                        }
                        error={outcome.errors.state}
                    >
                        {outcomes.map(({ group, items }) => (
                            <optgroup key={group} label={group}>
                                {items.map(item => (
                                    <option key={item.state} value={item.state}>
                                        {item.label}
                                    </option>
                                ))}
                            </optgroup>
                        ))}
                    </Select>

                    {selected && (
                        <Message variant={selected.danger ? 'error' : 'info'}>
                            {selected.effect}
                        </Message>
                    )}

                    <Textarea
                        label='Notitie (optioneel)'
                        rows={3}
                        value={outcome.data.note}
                        onChange={event =>
                            outcome.setData('note', event.target.value)
                        }
                        error={outcome.errors.note}
                    />

                    <div className='flex justify-end'>
                        <Button
                            type='submit'
                            variant={selected?.danger ? 'danger' : 'primary'}
                            disabled={!selected}
                            loading={outcome.processing}
                        >
                            {selected?.submitLabel ?? 'Belresultaat opslaan'}
                        </Button>
                    </div>
                </form>
            </Collapsible>

            <Collapsible title={`Belletjes (${calls.length})`}>
                {calls.length === 0 ? (
                    <p className='text-sm text-gray-500'>
                        Er zijn nog geen belletjes.
                    </p>
                ) : (
                    <ul className='flex flex-col gap-2 text-sm'>
                        {calls.map(call => (
                            <li
                                key={call.id}
                                className='flex items-start justify-between gap-2'
                            >
                                <div>
                                    <p className='text-gray-700'>
                                        {localDate(
                                            call.called_at ?? call.scheduled_at
                                        )}
                                        {call.caller && ` · ${call.caller}`}
                                    </p>
                                    {call.note && (
                                        <p className='text-gray-500'>
                                            {call.note}
                                        </p>
                                    )}
                                </div>
                                <Badge color={call.state_color}>
                                    {call.state_label}
                                </Badge>
                            </li>
                        ))}
                    </ul>
                )}
            </Collapsible>
        </SidePanel>
    );
};

export default PhoneCallPanel;
